import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
} from "@mui/material";
import PageHeader from "../../components/shared/PageHeader";
import PageCard from "../../components/shared/PageCard";
import { addButtonSx } from "../../components/shared/buttonStyles";
import { handleGetCompanies, handleDeleteCompany, handleGetCompanyUserCount } from "../../api/mosaic/accounts";
import { useConfirmDialog } from "../../components/shared/ConfirmDialogProvider";
import { useToast } from "../../components/shared/Toast";
import { CompanyTable } from "../../components/mosaic/Home/CompanyTable";
import AddCompanyModal from "../../components/mosaic/Home/AddCompanyModal";
import UpdateCompanyModal from "../../components/mosaic/Home/UpdateCompanyModal";
import UpdateCompanyPermissionsModal from "../../components/mosaic/Home/UpdateCompanyPermissionsModal";
import { useAppDispatch, useAppSelector } from "../../store/reduxHooks";
import { ICompanyData, setCompanyData } from "../../store/mosaic/companySlice";

const CompaniesPage: React.FC = () => {
  const user = useAppSelector((state) => state.userSlice.user);
  const companyData = useAppSelector((state) => state.companySlice.companyData);
  const dispatch = useAppDispatch();
  const { confirm } = useConfirmDialog();
  const { showSuccess, showError } = useToast();

  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isDoneSaving, setIsDoneSaving] = useState(false);

  const [openCompanyModal, setOpenCompanyModal] = useState(false);
  const [openUpdateCompanyModal, setOpenUpdateCompanyModal] = useState(false);
  const [openUpdatePermissionsModal, setOpenUpdatePermissionsModal] = useState(false);

  const [selectedRow, setSelectedRow] = useState({
    first_name: "" as string,
    last_name: "" as string,
    company_name: "" as string,
    action: "" as string,
    email: "" as string,
    company_id: "" as string,
    department: "" as string,
    id: "" as string | number,
  });

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setIsLoadingData(true);
        const companies = await handleGetCompanies();
        dispatch(setCompanyData(companies.data.data));
        setIsLoadingData(false);
      } catch (err) {
        setIsLoadingData(false);
        console.error(err);
      }
    };
    if (user) {
      fetchCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDoneSaving, user, dispatch]);

  const handleUpdateCompanyClick = (rowData: ICompanyData) => {
    setSelectedRow({
      first_name: "",
      last_name: "",
      company_name: rowData.name,
      department: "",
      action: "update",
      email: "",
      company_id: "",
      id: rowData.id,
    });
    setOpenUpdateCompanyModal(true);
  };

  const handleUpdatePermissionsCompanyClick = (rowData: ICompanyData) => {
    setSelectedRow({
      first_name: "",
      last_name: "",
      company_name: rowData.name,
      department: "",
      action: "update",
      email: "",
      company_id: "",
      id: rowData.id,
    });
    setOpenUpdatePermissionsModal(true);
  };

  const handleCreateClick = () => {
    setOpenCompanyModal(true);
  };

  const handleDelete = async (id: number) => {
    let count = 0;
    try {
      const result = await handleGetCompanyUserCount(id);
      count = result.data?.count ?? 0;
    } catch (err) {
      showError("Error fetching company details");
      console.error(err);
      return;
    }

    const message = count > 0
      ? `This company has ${count} active user(s). Deleting will deactivate them. Continue?`
      : "Are you sure you want to delete this company?";
    const confirmed = await confirm({ title: "Delete Company", message, confirmText: "Delete" });
    if (!confirmed) return;

    try {
      await handleDeleteCompany(id);
      setIsDoneSaving((prev) => !prev);
      showSuccess("Company deleted successfully");
    } catch (err) {
      showError("Error deleting company");
      console.error(err);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <PageHeader title="Companies" subtitle="Manage your companies and permissions" count={companyData.length}>
        <Button variant="contained" onClick={handleCreateClick} sx={addButtonSx}>
          Add new company
        </Button>
      </PageHeader>
      <PageCard>
        {isLoadingData ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <CompanyTable
            handleUpdateCompanyClick={handleUpdateCompanyClick}
            handleUpdatePermissionsCompanyClick={handleUpdatePermissionsCompanyClick}
            handleDelete={handleDelete}
          />
        )}
      </PageCard>

      {openUpdateCompanyModal && (
        <UpdateCompanyModal
          open={openUpdateCompanyModal}
          setOpen={setOpenUpdateCompanyModal}
          setIsDoneSaving={setIsDoneSaving}
          rowData={selectedRow}
        />
      )}

      {openUpdatePermissionsModal && (
        <UpdateCompanyPermissionsModal
          open={openUpdatePermissionsModal}
          setOpen={setOpenUpdatePermissionsModal}
          setIsDoneSaving={setIsDoneSaving}
          rowData={selectedRow}
        />
      )}

      {openCompanyModal && (
        <AddCompanyModal
          open={openCompanyModal}
          setOpen={setOpenCompanyModal}
          setIsDoneSaving={setIsDoneSaving}
        />
      )}
    </Box>
  );
};

export default CompaniesPage;
